import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ChatText, Building, EnvelopeSimple, Info, X, Package, Plus, ArrowClockwise, ArrowRight, Buildings, CreditCard, Warning, Sparkle, Database, FileArrowUp, ChartBar, Compass, Lightning, House, PenNib, Trash, ShieldCheck, CurrencyDollar } from "@phosphor-icons/react";
import { useTenantCtx } from "@/lib/pro";
import SignaturePad from "@/components/SignaturePad";

export default function Settings() {
  const [s, setS] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [inboxLog, setInboxLog] = useState([]);
  const [smsLog, setSmsLog] = useState([]);

  useEffect(() => {
    api.get("/settings").then(r => { setS(r.data); setF(r.data); });
    api.get("/inbox/log").then(r => setInboxLog(r.data));
    api.get("/sms/log").then(r => setSmsLog(r.data));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of ["company_name", "twilio_account_sid", "twilio_auth_token", "twilio_from_number"]) {
        if (f[k] && !String(f[k]).startsWith("****")) payload[k] = f[k];
      }
      if (typeof f.sms_enabled === "boolean") payload.sms_enabled = f.sms_enabled;
      if (typeof f.ai_damage_classifier_enabled === "boolean") payload.ai_damage_classifier_enabled = f.ai_damage_classifier_enabled;
      if (typeof f.auto_dispatch_enabled === "boolean") payload.auto_dispatch_enabled = f.auto_dispatch_enabled;
      if (f.auto_dispatch_mode) payload.auto_dispatch_mode = f.auto_dispatch_mode;
      if (typeof f.auto_dispatch_require_diagnosis === "boolean") payload.auto_dispatch_require_diagnosis = f.auto_dispatch_require_diagnosis;
      if (typeof f.tenant_portal_enabled === "boolean") payload.tenant_portal_enabled = f.tenant_portal_enabled;
      if (typeof f.tenant_portal_chat_enabled === "boolean") payload.tenant_portal_chat_enabled = f.tenant_portal_chat_enabled;
      if (typeof f.tenant_portal_schedule_enabled === "boolean") payload.tenant_portal_schedule_enabled = f.tenant_portal_schedule_enabled;
      if (Array.isArray(f.custom_tags)) payload.custom_tags = f.custom_tags;
      await api.post("/settings", payload);
      const r = await api.get("/settings");
      setS(r.data); setF(r.data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!s) return <AdminShell><div className="p-8 font-bold">Loading...</div></AdminShell>;

  const inboxUrl = `${API}/inbox/appfolio?token=${s.inbox_secret}`;
  const copy = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied");
        return;
      }
      throw new Error("Clipboard API unavailable");
    } catch (_) {
      // Fallback: temporary textarea + execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied");
      } catch (_) {
        toast.error("Copy failed — please copy manually");
      }
    }
  };

  return (
    <AdminShell>
      <div className="p-8 max-w-4xl">
        <h1 className="font-display font-black text-4xl tracking-tight">Settings</h1>
        <p className="mt-1 text-slate-500 font-medium">Company info, SMS, and integrations.</p>

        <SubscriptionCard/>
        <PropertyCapCard/>
        <MySignatureCard/>
        <OfficeNetworkCard/>
        <AiPhotoTagCard/>
        <VoiceNotesCard/>
        <PhotoWatermarkCard/>

        {/* Company */}
        <section className="mt-8 rugged-card p-6">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <Building weight="bold" size={22}/> Company
          </h2>
          <div className="mt-4">
            <label className="text-xs font-black uppercase tracking-wider">Company Name</label>
            <input value={f.company_name || ""} onChange={e => setF({ ...f, company_name: e.target.value })}
              data-testid="settings-company-name" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
          </div>
          <div className="mt-4">
            <label className="text-xs font-black uppercase tracking-wider">Crew Timezone</label>
            <div className="mt-1 text-[11px] text-slate-500 font-medium">
              Anchors "Today" / "Tomorrow" on the Sweeper Radar to the crew's day, not the dispatcher's laptop day. Set this to where your field techs work — critical if any dispatch happens from a different timezone (offshore VA teams, remote managers).
            </div>
            <select value={f.tenant_timezone || "America/New_York"}
              onChange={e => setF({ ...f, tenant_timezone: e.target.value })}
              data-testid="settings-tenant-timezone"
              className="mt-2 w-full h-11 px-3 border-2 border-[#0f172a] bg-white font-mono text-sm">
              <optgroup label="US">
                <option value="America/New_York">America/New_York (Eastern)</option>
                <option value="America/Chicago">America/Chicago (Central)</option>
                <option value="America/Denver">America/Denver (Mountain)</option>
                <option value="America/Phoenix">America/Phoenix (Arizona)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                <option value="America/Anchorage">America/Anchorage (Alaska)</option>
                <option value="Pacific/Honolulu">Pacific/Honolulu (Hawaii)</option>
              </optgroup>
              <optgroup label="Canada">
                <option value="America/Toronto">America/Toronto</option>
                <option value="America/Vancouver">America/Vancouver</option>
              </optgroup>
              <optgroup label="Other">
                <option value="Europe/London">Europe/London</option>
                <option value="Asia/Manila">Asia/Manila (Philippines)</option>
                <option value="UTC">UTC</option>
              </optgroup>
            </select>
          </div>
        </section>

        {/* Tenant Self-Service Portal — Roadmap #7 */}
        <section className="mt-6 rugged-card p-6 border-l-4 border-cyan-500" data-testid="settings-tenant-portal-card">
          <div className="flex items-center gap-2">
            <House weight="fill" size={22} className="text-cyan-600"/>
            <h2 className="font-display font-black text-xl uppercase">Tenant Portal</h2>
            <span className={`ml-auto px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${f.tenant_portal_enabled ? "bg-cyan-600 text-white" : "bg-slate-200 text-slate-700"}`}
              data-testid="settings-tenant-portal-status">
              {f.tenant_portal_enabled ? "ACTIVE" : "DORMANT"}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            Give tenants a "My Requests" tab beside the existing intake form. They come back to the same QR link, enter their phone, and see the status, ETA, and tech assigned to every WO they've filed. Off by default. Existing "Report Something" intake keeps working exactly as it does today when this is dormant.
          </p>

          <div className="mt-4 flex items-center justify-between p-3 border-2 border-cyan-300 bg-cyan-50">
            <div className="min-w-0">
              <div className="font-display font-black text-sm uppercase tracking-wide">Step 1 · Portal (History + Status)</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Tenant sees their prior WOs, live status, tech name, ETA, and can add more photos to open requests. Zero backend cost to leave off.
              </div>
            </div>
            <button
              data-testid="settings-tenant-portal-toggle"
              onClick={() => setF({ ...f, tenant_portal_enabled: !f.tenant_portal_enabled })}
              className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.tenant_portal_enabled ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-white text-[#0f172a] hover:bg-cyan-50"}`}>
              {f.tenant_portal_enabled ? "ON" : "OFF"}
            </button>
          </div>

          {f.tenant_portal_enabled && (
            <>
              <div className="mt-3 flex items-center justify-between p-3 border-2 border-cyan-300 bg-cyan-50" data-testid="settings-tenant-portal-chat-row">
                <div className="min-w-0">
                  <div className="font-display font-black text-sm uppercase tracking-wide">Step 2 · Two-Way Chat</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Tenant + your crew message each other inside the WO. Every message shows up on the admin Job Detail so nothing gets lost in SMS.
                  </div>
                </div>
                <button
                  data-testid="settings-tenant-portal-chat-toggle"
                  onClick={() => setF({ ...f, tenant_portal_chat_enabled: !f.tenant_portal_chat_enabled })}
                  className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.tenant_portal_chat_enabled ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-white text-[#0f172a] hover:bg-cyan-50"}`}>
                  {f.tenant_portal_chat_enabled ? "ON" : "OFF"}
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between p-3 border-2 border-cyan-300 bg-cyan-50" data-testid="settings-tenant-portal-schedule-row">
                <div className="min-w-0">
                  <div className="font-display font-black text-sm uppercase tracking-wide">Step 3 · Schedule Confirmation</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Propose a visit window from the WO. Tenant taps Accept / Decline / Reschedule inside their portal. No more phone tag.
                  </div>
                </div>
                <button
                  data-testid="settings-tenant-portal-schedule-toggle"
                  onClick={() => setF({ ...f, tenant_portal_schedule_enabled: !f.tenant_portal_schedule_enabled })}
                  className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.tenant_portal_schedule_enabled ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-white text-[#0f172a] hover:bg-cyan-50"}`}>
                  {f.tenant_portal_schedule_enabled ? "ON" : "OFF"}
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between p-3 border-2 border-cyan-300 bg-cyan-50" data-testid="settings-tenant-portal-notify-row">
                <div className="min-w-0">
                  <div className="font-display font-black text-sm uppercase tracking-wide">Step 4 · Auto-Notify on Assign</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Text the tenant their portal link the second a WO gets its first real assignee. Guarded — sent once per WO, only if Twilio + Portal are on, phone is 10+ digits, and the tenant filed the WO themselves. Rate-limited to 3 per phone / 24h.
                  </div>
                </div>
                <button
                  data-testid="settings-tenant-portal-notify-toggle"
                  onClick={() => setF({ ...f, tenant_notify_on_assign_enabled: !f.tenant_notify_on_assign_enabled })}
                  className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.tenant_notify_on_assign_enabled ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-white text-[#0f172a] hover:bg-cyan-50"}`}>
                  {f.tenant_notify_on_assign_enabled ? "ON" : "OFF"}
                </button>
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                Click <a href="/tenant-portal-preview" target="_blank" rel="noreferrer" className="underline font-black">/tenant-portal-preview</a> to preview the tenant UI without live data.
              </div>
            </>
          )}
        </section>

        {/* Auto-Complete on Photos */}
        <section className="mt-6 rugged-card p-6 border-l-4 border-emerald-500" data-testid="settings-autocomplete-card">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-black text-xl uppercase">Auto-Complete on Photos</h2>
            <span className={`ml-auto px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${f.auto_complete_on_after_photos_enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`}
              data-testid="settings-autocomplete-status">
              {f.auto_complete_on_after_photos_enabled ? "ACTIVE" : "DORMANT"}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            When a tech uploads the required after-photo(s), the WO auto-flips from ASSIGNED → COMPLETED so nothing gets stuck like PC-3360DC did. Fires once per WO — no double-completes. Uses `min_after_photos_to_complete` on the WO if set, otherwise the default below.
          </p>
          <div className="mt-4 flex items-center justify-between p-3 border-2 border-emerald-300 bg-emerald-50">
            <div className="min-w-0">
              <div className="font-display font-black text-sm uppercase tracking-wide">Activation</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Off by default — same reason as every other automation on this page.
              </div>
            </div>
            <button
              data-testid="settings-autocomplete-toggle"
              onClick={() => setF({ ...f, auto_complete_on_after_photos_enabled: !f.auto_complete_on_after_photos_enabled })}
              className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.auto_complete_on_after_photos_enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white text-[#0f172a] hover:bg-emerald-50"}`}>
              {f.auto_complete_on_after_photos_enabled ? "ON" : "OFF"}
            </button>
          </div>
          {f.auto_complete_on_after_photos_enabled && (
            <div className="mt-3 p-3 border-2 border-emerald-300 bg-emerald-50">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Default after-photo threshold</label>
              <input type="number" min="1" max="10" step="1"
                value={f.default_min_after_photos ?? 1}
                onChange={e => setF({ ...f, default_min_after_photos: Math.max(1, parseInt(e.target.value) || 1) })}
                data-testid="settings-autocomplete-threshold"
                className="mt-1 w-24 h-11 px-3 border-2 border-[#0f172a] text-lg font-mono font-bold"/>
              <div className="mt-1 text-[11px] text-slate-500">
                Number of after-photos before a WO auto-completes. Any individual WO can override with its own <code>min_after_photos_to_complete</code> field.
              </div>
            </div>
          )}
        </section>

        {/* Auto-Dispatch — Roadmap #6 */}
        <section className="mt-6 rugged-card p-6 border-l-4 border-fuchsia-500" data-testid="settings-auto-dispatch-card">
          <div className="flex items-center gap-2">
            <Compass weight="fill" size={22} className="text-fuchsia-600"/>
            <h2 className="font-display font-black text-xl uppercase">Auto-Dispatch</h2>
            <span className={`ml-auto px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${f.auto_dispatch_enabled ? "bg-fuchsia-600 text-white" : "bg-slate-200 text-slate-700"}`}
              data-testid="settings-autodispatch-status">
              {f.auto_dispatch_enabled ? "ACTIVE" : "DORMANT"}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            Match a work order to the best-fit worker by skill, distance from their home ZIP, and current job load.
            Off by default. Nothing dispatches until you flip the switch, and even then you can keep it in "Suggest" mode
            so it just recommends — you still tap the assign.
          </p>

          <div className="mt-4 flex items-center justify-between p-3 border-2 border-fuchsia-300 bg-fuchsia-50">
            <div className="min-w-0">
              <div className="font-display font-black text-sm uppercase tracking-wide">Activation</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Turns the whole feature on. Workers need a home ZIP + skills for good matches — set those in Team.
              </div>
            </div>
            <button
              data-testid="settings-autodispatch-toggle"
              onClick={() => setF({ ...f, auto_dispatch_enabled: !f.auto_dispatch_enabled })}
              className={`shrink-0 tactile-btn h-11 w-24 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] ${f.auto_dispatch_enabled ? "bg-fuchsia-600 text-white hover:bg-fuchsia-700" : "bg-white text-[#0f172a] hover:bg-fuchsia-50"}`}>
              {f.auto_dispatch_enabled ? "ON" : "OFF"}
            </button>
          </div>

          {f.auto_dispatch_enabled && (
            <div className="mt-3 space-y-3" data-testid="settings-autodispatch-options">
              <div>
                <label className="text-xs font-black uppercase tracking-wider">Mode</label>
                <select
                  value={f.auto_dispatch_mode || "suggest"}
                  onChange={e => setF({ ...f, auto_dispatch_mode: e.target.value })}
                  data-testid="settings-autodispatch-mode"
                  className="mt-1 w-full h-11 px-2 border-2 border-[#0f172a] bg-white">
                  <option value="suggest">Suggest — show top matches, you tap to assign</option>
                  <option value="auto">Auto — one tap assigns the top match automatically</option>
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  "Auto" still requires you to hit the Auto-Assign button on a job. Nothing routes without you.
                </p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox"
                  checked={!!f.auto_dispatch_require_diagnosis}
                  onChange={e => setF({ ...f, auto_dispatch_require_diagnosis: e.target.checked })}
                  data-testid="settings-autodispatch-require-dx"
                  className="mt-1"/>
                <div>
                  <div className="text-xs font-black uppercase tracking-wider">Only after AI Diagnosis</div>
                  <div className="text-[11px] text-slate-500">
                    Block dispatch until a job has an AI-Diagnose banner. Keeps low-signal jobs from routing to the wrong trade.
                  </div>
                </div>
              </label>
            </div>
          )}
        </section>

        {/* AppFolio Inbox */}
        <section className="mt-6 rugged-card p-6">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <EnvelopeSimple weight="bold" size={22}/> AppFolio Email Sync
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            Forward AppFolio work-order emails to your inbox URL below via Zapier or Make.com. Each email becomes a pool job workers can claim.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Inbox Endpoint</label>
              <div className="mt-1 flex flex-wrap gap-2">
                <input readOnly value={inboxUrl} className="flex-1 min-w-[240px] h-11 px-3 border-2 border-[#0f172a] font-mono text-xs bg-slate-50"
                  data-testid="settings-inbox-url"/>
                <button onClick={() => copy(inboxUrl)} data-testid="settings-inbox-copy-btn"
                  className="tactile-btn h-11 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1">
                  <Copy weight="bold" size={14}/> Copy
                </button>
                <button onClick={async () => {
                  if (!window.confirm("Rotate this inbox token?\n\nThe OLD token stops working immediately. You must paste the NEW token into Zapier / Cloudflare Worker to keep AppFolio emails flowing.")) return;
                  try {
                    const r = await api.post("/settings/regenerate-inbox-secret");
                    setS(prev => ({ ...prev, inbox_secret: r.data.inbox_secret }));
                    setF(prev => ({ ...prev, inbox_secret: r.data.inbox_secret }));
                    toast.success("New token generated. Update Zapier / Cloudflare Worker now.");
                  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rotate failed"); }
                }} data-testid="settings-inbox-rotate-btn"
                  className="tactile-btn h-11 px-4 bg-red-600 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1">
                  <ArrowClockwise weight="bold" size={14}/> Rotate
                </button>
              </div>
              <p className="mt-1.5 text-[11px] font-medium text-slate-500">
                Rotate if the token ever leaks. The old link stops working immediately — update Zapier / Cloudflare Worker with the new URL.
              </p>
            </div>
            <div className="p-3 bg-cyan-50 border-2 border-dashed border-[#00C4CC] text-xs">
              <div className="font-black uppercase tracking-wider flex items-center gap-1"><Info weight="fill" size={12}/> Zapier setup (3 min)</div>
              <ol className="mt-1 list-decimal pl-4 space-y-0.5 text-slate-700">
                <li>New Zap → Trigger: Email by Zapier (get a @robot.zapier.com address)</li>
                <li>Set an AppFolio email filter to forward to that address</li>
                <li>Action: Webhooks → POST → paste the Inbox Endpoint above → JSON body: <code>{`{"subject":"{{Subject}}","body":"{{Body Plain}}","from_address":"{{From}}"}`}</code></li>
              </ol>
            </div>
            <div className="p-3 bg-red-50 border-2 border-dashed border-red-500 text-xs" data-testid="settings-eta-callout">
              <div className="font-black uppercase tracking-wider flex items-center gap-1 text-red-800">
                <Warning weight="fill" size={12}/> Urgent ETA Requests · Auto-Flagged
              </div>
              <p className="mt-1 text-slate-700 leading-relaxed">
                When an AppFolio email lands with subject starting <code className="bg-white px-1">ETA:</code> (e.g.
                <em> "ETA: Work Order #3330 for 886 N 49th St..."</em>), Field Radar auto-flags it as an urgent ETA request
                so the red banner on your dispatch board catches it before the tenant/landlord starts pinging you.
                Admin gets a push notification instantly instead of workers.
              </p>
            </div>
          </div>
          <div className="mt-5">
            <h3 className="font-display font-black text-sm uppercase tracking-wider">Recent Inbox Activity</h3>
            {inboxLog.length === 0 ? (
              <div className="mt-2 text-sm text-slate-400 font-medium">No AppFolio emails received yet.</div>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {inboxLog.map((l, i) => (
                  <li key={i} className="flex items-center justify-between border-b py-2">
                    <span className="truncate flex-1">{l.subject || "(no subject)"}</span>
                    <span className="text-xs text-slate-500">{new Date(l.received_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Owner Approval Threshold — AppFolio-parity */}
        <OwnerApprovalCard/>

        {/* SMS */}
        <section className="mt-6 rugged-card p-6">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <ChatText weight="bold" size={22}/> SMS (Twilio)
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            {s.sms_enabled ? "SMS is LIVE" : <span><b>MOCKED</b> — Add Twilio credentials below and enable to send real messages.</span>}
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Twilio Account SID</label>
              <input value={f.twilio_account_sid || ""} onChange={e => setF({ ...f, twilio_account_sid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxxxx"
                data-testid="settings-twilio-sid" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono text-xs"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Twilio Auth Token</label>
              <input type="password" value={f.twilio_auth_token || ""} onChange={e => setF({ ...f, twilio_auth_token: e.target.value })}
                placeholder="Leave blank to keep current"
                data-testid="settings-twilio-token" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono text-xs"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Twilio From Number</label>
              <input value={f.twilio_from_number || ""} onChange={e => setF({ ...f, twilio_from_number: e.target.value })}
                placeholder="+15551234567"
                data-testid="settings-twilio-from" className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-mono text-xs"/>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 h-11">
                <input type="checkbox" checked={!!f.sms_enabled} onChange={e => setF({ ...f, sms_enabled: e.target.checked })}
                  data-testid="settings-sms-enabled" className="h-5 w-5 accent-[#00C4CC]"/>
                <span className="font-black uppercase text-sm tracking-wider">Enable SMS Sending</span>
              </label>
            </div>
          </div>
          <div className="mt-5">
            <h3 className="font-display font-black text-sm uppercase tracking-wider">Recent SMS ({smsLog.length})</h3>
            {smsLog.length === 0 ? (
              <div className="mt-2 text-sm text-slate-400 font-medium">No messages sent yet.</div>
            ) : (
              <ul className="mt-2 space-y-1 text-sm max-h-48 overflow-y-auto">
                {smsLog.slice(0, 8).map(l => (
                  <li key={l.id} className="flex items-center justify-between border-b py-2">
                    <span className="flex-1 truncate">{l.to}: {l.message}</span>
                    <span className={`text-xs font-bold uppercase px-2 ${l.status === "sent" ? "text-green-600" : l.status === "mocked" ? "text-amber-600" : "text-red-600"}`}>{l.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="mt-6 rugged-card p-6" data-testid="comms-silence-section">
          <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
            <Warning weight="fill" size={22} className="text-red-600"/> Comms Silence (Master Switch)
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            One switch to mute <b>every outbound email and text</b> to tenants, landlords, vendors, and customers. Optionally keep internal team-to-team comms flowing so your dispatchers and crew can still get messages.
          </p>
          <CommsSilenceCard/>
        </section>

        {/* AI Features */}
        <section className="mt-6 rugged-card p-6" data-testid="ai-features-section">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <Sparkle weight="fill" size={22}/> AI Features
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            Extra Claude-powered helpers that go beyond the standard AI trial. Turn each one on only when your team is ready to review its output.
          </p>
          <div className="mt-4 border-2 border-[#0f172a] bg-white p-4 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
                AI Damage Classifier
                <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-sm ${f.ai_damage_classifier_enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`} data-testid="ai-damage-classifier-status">
                  {f.ai_damage_classifier_enabled ? "ON" : "OFF"}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-600 font-medium leading-relaxed">
                Claude vision looks at any Turn photo and calls it <b>tenant damage</b> vs <b>normal wear-and-tear</b>, with severity, confidence, and a suggested chargeback range. Appears as an <b>"AI Classify"</b> button on every photo in the Chargeback panel. Uses your Universal Key credits.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 h-10 cursor-pointer shrink-0">
              <input type="checkbox"
                checked={!!f.ai_damage_classifier_enabled}
                onChange={e => setF({ ...f, ai_damage_classifier_enabled: e.target.checked })}
                data-testid="ai-damage-classifier-toggle"
                className="h-5 w-5 accent-[#00C4CC]"/>
              <span className="font-black uppercase text-xs tracking-wider">Enable</span>
            </label>
          </div>
        </section>

        {/* Custom Photo Tags */}
        <section className="mt-6 rugged-card p-6">
          <h2 className="font-display font-black text-xl uppercase">Custom Photo Tags</h2>
          <p className="mt-2 text-sm text-slate-600 font-medium">
            Add company-specific tags that appear in the photo tag picker (alongside the built-in tags like Before / After Repair / Damage Detail).
          </p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="custom-tags-list">
            {(f.custom_tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-wider">
                {t}
                <button onClick={() => setF({ ...f, custom_tags: (f.custom_tags || []).filter((_, idx) => idx !== i) })}
                  data-testid={`remove-tag-${t}`} className="ml-1 text-red-500"><X weight="bold" size={12}/></button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input placeholder="e.g., HVAC Panel, Roof Damage" value={f._newTag || ""}
              onChange={e => setF({ ...f, _newTag: e.target.value })}
              onKeyDown={e => {
                if (e.key === "Enter" && f._newTag?.trim()) {
                  setF(prev => ({ ...prev, custom_tags: [...(prev.custom_tags || []), prev._newTag.trim()], _newTag: "" }));
                }
              }}
              data-testid="new-tag-input"
              className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <button onClick={() => {
              if (f._newTag?.trim()) {
                setF(prev => ({ ...prev, custom_tags: [...(prev.custom_tags || []), prev._newTag.trim()], _newTag: "" }));
              }
            }} data-testid="add-tag-btn"
              className="tactile-btn h-11 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider">
              Add
            </button>
          </div>
        </section>

        <LegacyImportSection/>

        <ClickUpImportSection/>

        <PartsCatalogSection/>


        <div className="mt-6">
          <button onClick={save} disabled={busy} data-testid="settings-save-btn"
            className="tactile-btn h-12 px-8 bg-[#00C4CC] text-white font-black uppercase tracking-wider disabled:opacity-50">
            {busy ? "Saving..." : "Save Settings"}
          </button>
        </div>
        <PendingResets/>

      </div>
    </AdminShell>
  );
}


function OfficeNetworkCard() {
  // Corporate firewalls (SonicWall, Cisco Umbrella, Meraki, Fortinet, Palo
  // Alto, Zscaler, Cloudflare Gateway) block "uncategorized" SaaS by default.
  // Every mid-market customer hits this eventually — Ciarra was our first,
  // her partner unblocked it via IT, but any future PM company's office IT
  // will do the same dance. This card gives the admin a one-tap
  // "give-this-to-your-IT" PDF so they don't have to figure it out cold.
  const [downloading, setDownloading] = useState(false);
  const downloadIt = async () => {
    setDownloading(true);
    try {
      // We can't rely on <a href> because the endpoint is JWT-protected.
      // Fetch as blob, then trigger download.
      const t = localStorage.getItem("access_token");
      const r = await fetch(`${API}/settings/it-onepager.pdf`, {
        headers: { Authorization: `Bearer ${t || ""}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "FieldRadar-IT-Setup.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("IT Setup PDF downloaded — hand it to your office IT team");
    } catch (e) {
      toast.error("Couldn't generate IT PDF — try again in a moment");
    } finally {
      setDownloading(false);
    }
  };
  return (
    <section className="mt-6 rugged-card p-6 border-l-4 border-amber-500" data-testid="settings-office-network-card">
      <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
        <ShieldCheck weight="bold" size={22}/> Office Network Setup
      </h2>
      <p className="mt-2 text-sm text-slate-600 font-medium leading-snug">
        Can't reach Field Radar from your office? Most corporate firewalls block "uncategorized" web apps by default. Download the one-pager below and hand it to your office IT team — it lists the exact hostnames to allow, protocol requirements, and how to submit us to the major web-filter vendors so you never hit this again.
      </p>
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <button onClick={downloadIt} disabled={downloading}
          data-testid="settings-it-onepager-download"
          className="inline-flex items-center gap-2 px-4 h-11 bg-[#0F1F3A] hover:bg-[#00C4CC] disabled:opacity-60 text-white font-black uppercase text-xs tracking-widest transition">
          <FileArrowUp weight="fill" size={16}/>
          {downloading ? "Generating…" : "Download IT Setup PDF"}
        </button>
        <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">
          Pre-filled with your company name
        </span>
      </div>
      <details className="mt-4 text-xs text-slate-600">
        <summary className="cursor-pointer font-black uppercase tracking-widest text-slate-700 hover:text-[#0F1F3A]">
          What's inside the PDF?
        </summary>
        <ul className="mt-2 ml-4 list-disc space-y-1 leading-snug">
          <li>Hostnames to allow (<code className="font-mono">fieldradar.pro</code>, <code className="font-mono">*.fieldradar.pro</code>, backups)</li>
          <li>Protocol requirements (HTTPS/443, WebSocket, no SSL inspection)</li>
          <li>Filter category recommendation ("Business Applications / SaaS")</li>
          <li>Direct submission links for Cisco, Cloudflare, SonicWall, Fortinet, Palo Alto, Zscaler</li>
          <li>Verification steps + phone-hotspot workaround for blocked staff</li>
          <li>Field Radar security contact</li>
        </ul>
      </details>
    </section>
  );
}

function AiPhotoTagCard() {
  // Cost-conscious auto AI tagging.
  // Default OFF (Andrew asked); admins opt-in with a visible daily cap
  // and today's usage counter + estimated $ so nothing sneaks up on them.
  const [s, setS] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/settings");
      setS(r.data || {});
    } catch {}
    try {
      const r = await api.get("/settings/ai-tag-usage");
      setUsage(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  const patch = async (patchBody) => {
    setBusy(true);
    try {
      await api.patch("/settings", patchBody);
      await load();
    } finally { setBusy(false); }
  };
  if (!s) return null;
  const enabled = !!s.photo_ai_tag_enabled;
  const cap = s.photo_ai_tag_daily_cap || 200;
  return (
    <section className="mt-6 rugged-card p-6 border-l-4 border-cyan-500" data-testid="settings-ai-tag-card">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            🤖 AI Auto-tag Photos
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium leading-snug">
            Turn on to automatically classify every new photo with tags like "Damage Detail · Plumbing" and a short AI note. Enables cross-property search ("show every leak photo") in the Photo Library. <b>Costs about $0.005 per photo</b> (~$15/mo for 100 photos/day).
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-ai-tag-toggle">
          <input type="checkbox" className="sr-only peer" checked={enabled} disabled={busy}
            onChange={(e) => patch({ photo_ai_tag_enabled: e.target.checked })}/>
          <span className="h-7 w-12 rounded-full bg-slate-300 peer-checked:bg-[#00C4CC] transition relative">
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${enabled ? "left-[22px]" : "left-0.5"}`}/>
          </span>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{enabled ? "On" : "Off"}</span>
        </label>
      </div>
      {enabled && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Today's Usage</div>
            <div className="mt-1 font-black text-2xl text-[#0F1F3A]" data-testid="settings-ai-tag-count">
              {usage?.count ?? 0} <span className="text-xs text-slate-500 font-bold">/ {cap}</span>
            </div>
            <div className="text-[10px] text-slate-500 font-bold mt-0.5">≈ ${(usage?.est_cost_today_usd ?? 0).toFixed(3)} spent today</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Daily Cap</div>
            <input type="number" min="1" max="10000" defaultValue={cap}
              data-testid="settings-ai-tag-cap"
              onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== cap) patch({ photo_ai_tag_daily_cap: v }); }}
              className="w-full mt-1 h-9 px-2 border-2 border-slate-300 rounded text-lg font-black text-[#0F1F3A] focus:border-cyan-500 focus:outline-none"/>
            <div className="text-[10px] text-slate-500 font-bold mt-0.5">photos/day · after this we stop auto-tagging</div>
          </div>
          <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-cyan-700">Manual Anytime</div>
            <div className="mt-1 text-[11px] text-cyan-900 font-medium leading-snug">
              You can still hit the "AI Tag" batch button on any WO to tag specific photos on demand, even when auto-tagging is off.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function VoiceNotesCard() {
  // Tenant-wide switch for whether techs can attach voice memos to
  // individual photos. Andrew's call (option b): office never sees the
  // record button; playback shows for everyone. This card only controls
  // whether recording is *allowed at all* for the crew.
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/settings"); setS(r.data || {}); } catch {}
  };
  useEffect(() => { load(); }, []);
  const patch = async (patchBody) => {
    setBusy(true);
    try { await api.patch("/settings", patchBody); await load(); }
    finally { setBusy(false); }
  };
  if (!s) return null;
  const enabled = s.photo_voice_notes_enabled !== false; // default ON
  return (
    <section className="mt-6 rugged-card p-6 border-l-4 border-purple-500" data-testid="settings-voice-notes-card">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            🎙️ Voice Notes on Photos
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium leading-snug">
            When ON, technicians and subs can hold to record a 30-second voice memo pinned to any photo — great for dirty-hands moments where typing a caption is slow. Office never sees a record button; you'll get inline playback on every voice-noted photo. Existing memos are preserved if you flip this off later.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-voice-notes-toggle">
          <input type="checkbox" className="sr-only peer" checked={enabled} disabled={busy}
            onChange={(e) => patch({ photo_voice_notes_enabled: e.target.checked })}/>
          <span className="h-7 w-12 rounded-full bg-slate-300 peer-checked:bg-purple-600 transition relative">
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${enabled ? "left-[22px]" : "left-0.5"}`}/>
          </span>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{enabled ? "On (Techs)" : "Off"}</span>
        </label>
      </div>
    </section>
  );
}

function PhotoWatermarkCard() {
  // Court-ready photo watermark burn.
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/settings"); setS(r.data || {}); } catch {}
  };
  useEffect(() => { load(); }, []);
  const patch = async (patchBody) => {
    setBusy(true);
    try { await api.patch("/settings", patchBody); await load(); }
    finally { setBusy(false); }
  };
  if (!s) return null;
  const enabled = !!s.photo_watermark_enabled;
  const weatherOn = !!s.photo_weather_stamp_enabled;
  return (
    <section className="mt-6 rugged-card p-6 border-l-4 border-slate-800" data-testid="settings-watermark-card">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            📸 Court-ready Watermark
          </h2>
          <p className="mt-2 text-sm text-slate-600 font-medium leading-snug">
            Burn timestamp, GPS, and company name onto every uploaded photo (Before / After / Marked-up) so the evidence survives screenshots and re-shares. Original untouched frame is still preserved.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-watermark-toggle">
          <input type="checkbox" className="sr-only peer" checked={enabled} disabled={busy}
            onChange={(e) => patch({ photo_watermark_enabled: e.target.checked })}/>
          <span className="h-7 w-12 rounded-full bg-slate-300 peer-checked:bg-slate-900 transition relative">
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${enabled ? "left-[22px]" : "left-0.5"}`}/>
          </span>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{enabled ? "On" : "Off"}</span>
        </label>
      </div>
      {/* Weather stamp piggybacks on the watermark strip — only shown
          when the watermark itself is on. */}
      {enabled && (
        <div className="mt-4 pt-4 border-t border-slate-200 flex items-start gap-3" data-testid="settings-weather-row">
          <div className="flex-1">
            <div className="font-display font-black text-sm uppercase flex items-center gap-2 tracking-wider">
              ⛅ Weather Stamp
            </div>
            <p className="mt-1 text-xs text-slate-600 font-medium leading-snug">
              Also burn current temp + condition (e.g. "34°F · Snow") into the watermark strip when the photo has GPS. Perfect proof for snow-plow trip charges and storm damage claims. Uses Open-Meteo — free, no key required.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-weather-toggle">
            <input type="checkbox" className="sr-only peer" checked={weatherOn} disabled={busy}
              onChange={(e) => patch({ photo_weather_stamp_enabled: e.target.checked })}/>
            <span className="h-7 w-12 rounded-full bg-slate-300 peer-checked:bg-slate-900 transition relative">
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${weatherOn ? "left-[22px]" : "left-0.5"}`}/>
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{weatherOn ? "On" : "Off"}</span>
          </label>
        </div>
      )}
    </section>
  );
}

function MySignatureCard() {
  // Auto-stamped above the SUPERVISOR line on every WO PDF the user
  // completes. Tap "Sign or re-sign" → SignaturePad modal → the drawn
  // dataUrl POSTs to /api/me/signature; a fresh <img> with a cache-bust
  // param re-renders the preview.
  const [padOpen, setPadOpen] = useState(false);
  const [hasSig, setHasSig] = useState(null); // null=loading, false=none, true=exists
  const [bust, setBust] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const t = localStorage.getItem("access_token");
      const r = await fetch(`${API}/me/signature?auth=${encodeURIComponent(t || "")}`);
      setHasSig(r.ok);
    } catch { setHasSig(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSig = async ({ dataUrl }) => {
    setBusy(true);
    try {
      await api.post("/me/signature", { data_url: dataUrl });
      setBust(Date.now());
      setHasSig(true);
      setPadOpen(false);
      toast.success("Signature saved — will stamp on every WO PDF you sign off from now on");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally { setBusy(false); }
  };
  const removeSig = async () => {
    if (!window.confirm("Remove your saved signature?")) return;
    try {
      await api.delete("/me/signature");
      setHasSig(false);
      toast.success("Signature removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Remove failed");
    }
  };

  return (
    <section className="mt-6 rugged-card p-6" data-testid="my-signature-card">
      <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
        <PenNib weight="fill" size={22} className="text-indigo-500"/> My Signature
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Auto-stamped above the SUPERVISOR line on every WO PDF you sign off. Draw it once — reused forever.
      </p>

      <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
        <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded p-3 flex-shrink-0" style={{ minWidth: 260, minHeight: 90 }}>
          {hasSig === null && <div className="text-xs text-slate-400 italic">Loading…</div>}
          {hasSig === false && (
            <div className="text-xs text-slate-400 italic flex items-center justify-center h-16">
              No signature on file
            </div>
          )}
          {hasSig === true && (
            <img
              src={`${API}/me/signature?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}&v=${bust}`}
              alt="Your signature"
              className="max-h-20"
              data-testid="my-signature-preview"/>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => setPadOpen(true)}
            disabled={busy}
            data-testid="my-signature-sign-btn"
            className="h-10 px-4 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-xs tracking-widest rounded disabled:opacity-60">
            <PenNib weight="bold" size={14}/>
            {hasSig ? "Re-sign" : "Sign"}
          </button>
          {hasSig && (
            <button
              onClick={removeSig}
              data-testid="my-signature-remove-btn"
              className="h-9 px-3 inline-flex items-center gap-1 border-2 border-rose-300 text-rose-600 hover:bg-rose-50 font-black uppercase text-[10px] tracking-widest rounded">
              <Trash weight="bold" size={11}/> Remove
            </button>
          )}
        </div>
      </div>

      {padOpen && (
        <SignaturePad
          defaultName=""
          onCancel={() => setPadOpen(false)}
          onSave={saveSig}/>
      )}
    </section>
  );
}


function SubscriptionCard() {
  const [b, setB] = useState(null);
  const { tenant } = useTenantCtx();
  useEffect(() => {
    api.get("/billing/status").then(r => setB(r.data)).catch(() => {});
  }, []);
  if (!b) return null;
  const status = b.subscription_status;
  const days = b.subscription_days_left;
  const tier = b.plan_tier || tenant?.plan_tier || "pro";

  const badge = {
    trial: { label: `Trial · ${days ?? 0} day${days === 1 ? "" : "s"} left`, cls: days != null && days <= 2 ? "bg-red-600 text-white" : "bg-violet-600 text-white" },
    active: { label: "Active", cls: "bg-green-600 text-white" },
    expired: { label: "Trial Expired", cls: "bg-red-700 text-white" },
    grandfathered: { label: "Legacy Account", cls: "bg-slate-900 text-white" },
  }[status] || { label: status, cls: "bg-slate-500 text-white" };

  return (
    <section className="mt-8 rugged-card p-6" data-testid="subscription-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <CreditCard weight="bold" size={22}/> Subscription
          </h2>
          <p className="mt-1 text-sm text-slate-600 font-medium">Your plan, trial window, and payment status.</p>
        </div>
        <span className={`px-3 py-1 font-black uppercase tracking-widest text-[11px] rounded-full ${badge.cls}`} data-testid="subscription-status-badge">
          {badge.label}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border-2 border-[#0f172a] p-3 bg-white">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Plan Tier</div>
          <div className="mt-1 font-display font-black text-lg capitalize" data-testid="subscription-plan-tier">{tier}</div>
        </div>
        <div className="border-2 border-[#0f172a] p-3 bg-white">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Overage This Month</div>
          <div className="mt-1 font-display font-black text-lg text-slate-800" data-testid="subscription-overage">
            ${(b.overage_amount || 0).toFixed(2)}
          </div>
        </div>
        <div className="border-2 border-[#0f172a] p-3 bg-white">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Trial Ends</div>
          <div className="mt-1 font-bold text-sm" data-testid="subscription-trial-ends">
            {b.subscription_trial_ends_at ? new Date(b.subscription_trial_ends_at).toLocaleDateString() : "—"}
          </div>
        </div>
      </div>
      {status === "expired" && (
        <div className="mt-4 p-3 bg-red-50 border-2 border-dashed border-red-500 text-xs font-bold text-red-800 flex items-start gap-2" data-testid="subscription-expired-notice">
          <Warning weight="fill" size={16} className="flex-shrink-0 mt-0.5"/>
          <span>Your trial has ended. Read-only mode is on — new jobs, edits, and imports are paused until you subscribe.</span>
        </div>
      )}
      {status === "trial" && days != null && days <= 2 && (
        <div className="mt-4 p-3 bg-amber-50 border-2 border-dashed border-amber-500 text-xs font-bold text-amber-900 flex items-start gap-2">
          <Warning weight="fill" size={16} className="flex-shrink-0 mt-0.5"/>
          <span>Your trial ends in {days} day{days === 1 ? "" : "s"}. Add a payment method now to avoid interruption.</span>
        </div>
      )}
    </section>
  );
}

function PropertyCapCard() {
  const [b, setB] = useState(null);
  useEffect(() => {
    api.get("/billing/status").then(r => setB(r.data)).catch(() => {});
  }, []);
  if (!b) return null;
  const used = b.property_count || 0;
  const cap = b.property_cap || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const barCls = pct >= 100 ? "bg-red-600" : pct >= 85 ? "bg-amber-500" : "bg-[#00C4CC]";
  const overCap = Math.max(0, used - cap);

  return (
    <section className="mt-6 rugged-card p-6" data-testid="property-cap-card">
      <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
        <Buildings weight="bold" size={22}/> Property Cap
      </h2>
      <p className="mt-1 text-sm text-slate-600 font-medium">How many unique addresses you have on file vs. your plan's limit.</p>

      <div className="mt-5 flex items-baseline justify-between">
        <div>
          <span className="font-display font-black text-4xl tracking-tight" data-testid="property-cap-used">{used}</span>
          <span className="ml-2 text-lg font-bold text-slate-500">/ <span data-testid="property-cap-limit">{cap.toLocaleString()}</span> properties</span>
        </div>
        <span className="text-xs font-black uppercase tracking-widest text-slate-500">
          <span data-testid="property-cap-overage">${(b.overage_amount || 0).toFixed(0)}</span> overage this month
        </span>
      </div>

      <div className="mt-3 h-3 bg-slate-200 rounded-full overflow-hidden" data-testid="property-cap-bar">
        <div className={`h-full ${barCls} transition-all`} style={{ width: `${pct}%` }} data-testid="property-cap-bar-fill"/>
      </div>
      <div className="mt-2 flex justify-between text-[11px] font-black uppercase tracking-widest text-slate-500">
        <span>{pct}% used</span>
        <span>{Math.max(0, cap - used).toLocaleString()} slots left</span>
      </div>

      {overCap > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border-2 border-dashed border-amber-500 text-xs font-bold text-amber-900" data-testid="property-cap-overage-notice">
          You're {overCap.toLocaleString()} properties over your cap. Overage billing kicks in once Stripe is wired.
        </div>
      )}
    </section>
  );
}

function PendingResets() {
  const [tickets, setTickets] = useState([]);
  const [pw, setPw] = useState({});
  const load = () => api.get("/auth/reset-tickets").then(r => setTickets(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (t) => {
    const p = pw[t.id];
    if (!p || p.length < 6) return toast.error("Enter a new password (min 6)");
    try {
      await api.post("/auth/approve-reset", { ticket_id: t.id, new_password: p });
      toast.success(`Password set for ${t.user_name}. Share this password with them.`);
      setPw(prev => ({ ...prev, [t.id]: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (tickets.length === 0) return null;
  return (
    <section className="mt-8 rugged-card p-6 bg-yellow-50 border-yellow-500" data-testid="pending-resets">
      <h2 className="font-display font-black text-xl uppercase">Pending Password Resets ({tickets.length})</h2>
      <p className="mt-1 text-sm text-slate-600 font-medium">These techs asked for a new password. Set one and share it with them directly.</p>
      <ul className="mt-4 space-y-3">
        {tickets.map(t => (
          <li key={t.id} className="flex flex-wrap items-center gap-2 border-2 border-[#0f172a] bg-white p-3">
            <div className="flex-1 min-w-[180px]">
              <div className="font-bold">{t.user_name}</div>
              <div className="text-xs text-slate-500">{t.email} · {new Date(t.created_at).toLocaleString()}</div>
            </div>
            <input type="password" placeholder="New password" minLength={6}
              value={pw[t.id] || ""} onChange={e => setPw(prev => ({ ...prev, [t.id]: e.target.value }))}
              data-testid={`reset-pw-${t.id}`}
              className="h-10 px-3 border-2 border-[#0f172a] w-40"/>
            <button onClick={() => approve(t)} data-testid={`reset-approve-${t.id}`}
              className="tactile-btn h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider">
              Set &amp; Approve
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}


function CommsSilenceCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  // draft state (edits before save)
  const [draft, setDraft] = useState({
    external_muted: false,
    internal_passthrough: false,
    allowed_user_ids: [],
    allowed_roles: [],
  });

  const load = async () => {
    try {
      const r = await api.get("/system/comms-silence");
      setData(r.data);
      setDraft({
        external_muted: !!r.data.config.external_muted,
        internal_passthrough: !!r.data.config.internal_passthrough,
        allowed_user_ids: r.data.config.allowed_user_ids || [],
        allowed_roles: r.data.config.allowed_roles || [],
      });
      setDirty(false);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/system/comms-silence", draft);
      toast.success(draft.external_muted
        ? "Comms SILENCED — external email + SMS blocked"
        : "Comms LIVE — all channels sending");
      await load();
      window.dispatchEvent(new CustomEvent("killswitch-changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save Comms Silence");
    } finally { setBusy(false); }
  };

  const setField = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setDirty(true); };

  const toggleRole = (role) => {
    setDraft(d => {
      const has = d.allowed_roles.includes(role);
      return { ...d, allowed_roles: has ? d.allowed_roles.filter(r => r !== role) : [...d.allowed_roles, role] };
    });
    setDirty(true);
  };

  const toggleUser = (uid) => {
    setDraft(d => {
      const has = d.allowed_user_ids.includes(uid);
      return { ...d, allowed_user_ids: has ? d.allowed_user_ids.filter(u => u !== uid) : [...d.allowed_user_ids, uid] };
    });
    setDirty(true);
  };

  if (!data) return <div className="mt-3 text-xs text-slate-500">Loading…</div>;

  const stateBg = draft.external_muted ? "bg-red-50 border-red-600" : "bg-emerald-50 border-emerald-600";
  const pillBg  = draft.external_muted ? "bg-red-600 text-white" : "bg-emerald-600 text-white";
  const pillTxt = draft.external_muted ? "SILENCED — nothing external is going out" : "LIVE — everything is sending";
  const users   = data.users || [];
  const filteredUsers = users.filter(u => {
    const q = (userSearch || "").trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.role || "").toLowerCase().includes(q);
  });
  const pickedUsers = users.filter(u => draft.allowed_user_ids.includes(u.id));

  return (
    <div className="mt-4 space-y-3">
      {/* State banner */}
      <div className={`p-4 border-2 ${stateBg} flex items-center justify-between gap-3`} data-testid="comms-silence-state-banner">
        <div className="min-w-0">
          <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-sm ${pillBg}`}>
            {pillTxt}
          </span>
          {data.config.updated_at && (
            <div className="text-[10px] text-slate-600 mt-2">
              Last saved <b>{new Date(data.config.updated_at).toLocaleString()}</b>
              {" "}(<code className="text-[10px]">{data.config.updated_at}</code>)
              {data.config.updated_by_name ? ` by ${data.config.updated_by_name}` : ""}
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-1">
            Verified from DB at {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Master switch */}
      <div className="p-4 border-2 border-[#0f172a] bg-white flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-black uppercase text-sm tracking-wider">Master — mute all external comms</div>
          <div className="text-xs text-slate-600 mt-1 font-medium">
            Covers email <b>and</b> SMS to every tenant, landlord, vendor, customer, and portal user.
          </div>
        </div>
        <button
          onClick={() => setField("external_muted", !draft.external_muted)}
          disabled={busy}
          data-testid="comms-silence-master-toggle"
          className={`shrink-0 tactile-btn h-11 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] disabled:opacity-50 ${draft.external_muted ? "bg-red-600 text-white hover:bg-red-700" : "bg-white text-slate-900 hover:bg-slate-100"}`}
        >
          {draft.external_muted ? "MUTED" : "LIVE"}
        </button>
      </div>

      {/* Internal passthrough */}
      <div className={`p-4 border-2 border-[#0f172a] bg-white ${draft.external_muted ? "" : "opacity-60"}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-black uppercase text-sm tracking-wider">Internal team passthrough</div>
            <div className="text-xs text-slate-600 mt-1 font-medium">
              When ON, messages still send if the recipient is on your team (matched by email or phone) AND is either picked below OR has one of the picked roles.
            </div>
          </div>
          <button
            onClick={() => setField("internal_passthrough", !draft.internal_passthrough)}
            disabled={busy || !draft.external_muted}
            data-testid="comms-silence-passthrough-toggle"
            className={`shrink-0 tactile-btn h-11 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] disabled:opacity-50 ${draft.internal_passthrough ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white text-slate-900 hover:bg-slate-100"}`}
          >
            {draft.internal_passthrough ? "ON" : "OFF"}
          </button>
        </div>

        {draft.internal_passthrough && (
          <div className="mt-4 space-y-3">
            {/* Role picker */}
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider mb-2">Whole roles that stay open:</div>
              <div className="flex flex-wrap gap-2">
                {(data.role_options || []).map(role => {
                  const on = draft.allowed_roles.includes(role);
                  return (
                    <button
                      key={role}
                      onClick={() => toggleRole(role)}
                      data-testid={`comms-silence-role-${role}`}
                      className={`px-3 h-9 text-xs font-black uppercase tracking-wider border-2 border-[#0f172a] tactile-btn ${on ? "bg-[#0f172a] text-white" : "bg-white text-slate-900 hover:bg-slate-100"}`}
                    >
                      {role}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* User picker */}
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider mb-2">Or pick specific users:</div>
              {pickedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {pickedUsers.map(u => (
                    <span key={u.id} className="inline-flex items-center gap-1 px-2 py-1 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-wider">
                      {u.name}
                      <button onClick={() => toggleUser(u.id)} className="ml-1 hover:text-red-300" data-testid={`comms-silence-user-remove-${u.id}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setPickerOpen(o => !o)}
                data-testid="comms-silence-user-picker-toggle"
                className="tactile-btn h-9 px-3 text-xs font-black uppercase tracking-wider border-2 border-[#0f172a] bg-white hover:bg-slate-100"
              >
                {pickerOpen ? "Close picker" : `Add users (${pickedUsers.length} picked)`}
              </button>
              {pickerOpen && (
                <div className="mt-2 border-2 border-[#0f172a] bg-white p-2 max-h-60 overflow-auto">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search name, email, or role…"
                    data-testid="comms-silence-user-search"
                    className="w-full h-9 px-2 border-2 border-slate-300 text-sm mb-2"
                  />
                  <ul className="space-y-1">
                    {filteredUsers.map(u => {
                      const on = draft.allowed_user_ids.includes(u.id);
                      return (
                        <li key={u.id}>
                          <button
                            type="button"
                            onClick={() => toggleUser(u.id)}
                            data-testid={`comms-silence-user-${u.id}`}
                            className={`w-full text-left px-2 py-1.5 text-xs border ${on ? "bg-emerald-50 border-emerald-600" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                          >
                            <span className="font-black">{u.name}</span>
                            <span className="text-slate-500"> — {u.email || "no email"}{u.role ? ` · ${u.role}` : ""}</span>
                          </button>
                        </li>
                      );
                    })}
                    {filteredUsers.length === 0 && <li className="text-xs text-slate-500 p-2">No matches.</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] text-slate-500">
          {data.legacy && (data.legacy.email_killswitch_enabled || data.legacy.sms_killswitch_enabled) && !draft.external_muted && (
            <span className="text-amber-700 font-black">Legacy killswitch was ON — saving will clear it.</span>
          )}
        </div>
        <button
          onClick={save}
          disabled={busy || !dirty}
          data-testid="comms-silence-save-btn"
          className="tactile-btn h-11 px-6 bg-[#0f172a] text-white font-black uppercase tracking-wider text-xs disabled:opacity-40"
        >
          {busy ? "Saving…" : (dirty ? "Save changes" : "Saved")}
        </button>
      </div>
    </div>
  );
}


function EmailKillswitchToggle() {
  const [ks, setKs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/system/email-killswitch"); setKs(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const flip = async (next) => {
    setBusy(true);
    try {
      await api.post("/system/email-killswitch", { enabled: next });
      toast.success(next ? "Emails BLOCKED — test mode ON" : "Emails ENABLED — live mode");
      await load();
      window.dispatchEvent(new CustomEvent("killswitch-changed"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not flip killswitch");
    } finally { setBusy(false); }
  };

  if (!ks) return <div className="mt-3 text-xs text-slate-500">Loading…</div>;
  return (
    <div className="mt-4 p-3 border-2 border-[#0f172a] bg-white flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
          {ks.enabled ? (
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white rounded-sm">TEST MODE (Blocked)</span>
          ) : (
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white rounded-sm">LIVE (Emails Sending)</span>
          )}
          {ks.env_locked && (
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-700 text-white rounded-sm" title="EMAIL_KILLSWITCH is force-enabled in /app/backend/.env">
              Env Locked
            </span>
          )}
        </div>
        {ks.updated_at && (
          <div className="text-[10px] text-slate-500 mt-1">
            Last flipped {new Date(ks.updated_at).toLocaleString()} by {ks.updated_by_name || "—"}
          </div>
        )}
      </div>
      <button onClick={() => flip(!ks.enabled)} disabled={busy || ks.env_locked}
        data-testid="email-killswitch-toggle-btn"
        title={ks.env_locked ? "Remove EMAIL_KILLSWITCH from /app/backend/.env to control this from the UI" : ""}
        className={`shrink-0 tactile-btn h-11 px-4 font-black uppercase tracking-wider text-xs border-2 border-[#0f172a] disabled:opacity-50 disabled:cursor-not-allowed ${ks.enabled ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}`}>
        {busy ? "Flipping…" : (ks.enabled ? "Enable Emails" : "Block Emails")}
      </button>
    </div>
  );
}

function LegacyImportSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.get("/admin/legacy-report").then(r => setCount(r.data?.total || 0)).catch(() => setCount(null));
  }, [result]);

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/legacy-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 rugged-card p-6" data-testid="legacy-import-section">
      <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
        <FileArrowUp weight="fill" size={22} className="text-amber-500"/> Import Legacy CSV
      </h2>
      <p className="mt-2 text-sm text-slate-600 font-medium">
        Upload an AppFolio work-order CSV export. Rows are stored as <b>archived</b> — they power Property History &amp; Tenant History but never show up in your active board / map / lineup. Dedupes automatically by <span className="font-mono">Work Order Number</span>.
      </p>
      {/* Nudge users towards the newer AppFolio importer if they want status/vendor refreshes on existing WOs. */}
      <Link
        to="/admin/imports/appfolio"
        data-testid="legacy-import-appfolio-nudge"
        className="mt-3 flex items-start gap-3 p-3 border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 transition-colors">
        <Warning weight="fill" size={20} className="text-amber-600 flex-shrink-0 mt-0.5"/>
        <div className="flex-1">
          <div className="font-black uppercase text-[11px] tracking-widest text-amber-900">
            Trying to refresh existing WOs?
          </div>
          <div className="text-xs text-slate-700 mt-0.5">
            This Legacy importer only <b>inserts new rows</b> — duplicates get skipped without updating. To refresh status, vendor, and dates on WOs already in your archive, use the <b>AppFolio Import</b> page (has an "update existing" toggle).
          </div>
          <div className="mt-1 text-[11px] font-black uppercase tracking-widest text-amber-700 inline-flex items-center gap-1">
            Go to AppFolio Import <ArrowRight weight="bold" size={12}/>
          </div>
        </div>
      </Link>
      {count !== null && count > 0 && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 border-2 border-amber-500 bg-amber-50 text-amber-900 font-black uppercase text-xs tracking-wider" data-testid="legacy-import-count">
          <Database weight="fill" size={14}/> {count.toLocaleString()} archived rows in the system
          <Link to="/admin/legacy-report" className="ml-2 text-[#00C4CC] hover:underline inline-flex items-center gap-1" data-testid="view-legacy-report-link">
            <ChartBar weight="bold" size={12}/> View Report →
          </Link>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <input type="file" accept=".csv,text/csv"
          onChange={e => setFile(e.target.files?.[0] || null)}
          data-testid="legacy-import-file"
          className="text-xs font-medium"/>
        <button onClick={upload} disabled={busy || !file}
          data-testid="legacy-import-upload-btn"
          className="tactile-btn h-11 px-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2">
          <FileArrowUp weight="fill" size={14}/> {busy ? "Importing…" : "Upload &amp; Import"}
        </button>
      </div>
      {result && (
        <div className="mt-3 p-3 border-2 border-emerald-500 bg-emerald-50 text-xs font-mono" data-testid="legacy-import-result">
          <div className="font-black uppercase tracking-widest text-emerald-800 mb-1">Import Complete</div>
          <div>• Rows in file: <b>{result.total_rows}</b></div>
          <div>• Newly imported: <b>{result.imported}</b></div>
          <div>• Skipped duplicates: <b>{result.skipped_duplicates}</b></div>
          <div>• Skipped malformed: <b>{result.skipped_malformed}</b></div>
          <div>• New property configs: <b>{result.new_property_configs}</b></div>
        </div>
      )}
    </section>
  );
}

function ClickUpImportSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/clickup-report").then(r => setStats(r.data)).catch(() => setStats(null));
  }, [result]);

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/clickup-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} ClickUp tasks`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 rugged-card p-6" data-testid="clickup-import-section">
      <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
        <FileArrowUp weight="fill" size={22} className="text-fuchsia-500"/> Import ClickUp CSV
      </h2>
      <p className="mt-2 text-sm text-slate-600 font-medium">
        Pull historical tasks out of ClickUp. Rows land as <b>first-class jobs</b> — status, priority, dates, notes, and attachment URLs are preserved so completed work goes to Completed and open work shows on the board. Dedupes by <span className="font-mono">Task ID</span>. Accepts flexible column names (Task Name, Address/Property Address, Status, Priority, Date Created, Attachments, Comments, Assignees, Tags).
      </p>
      {stats && stats.total > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="clickup-import-count">
          <span className="inline-flex items-center gap-2 px-3 py-1 border-2 border-fuchsia-500 bg-fuchsia-50 text-fuchsia-900 font-black uppercase text-xs tracking-wider">
            <Database weight="fill" size={14}/> {stats.total.toLocaleString()} tasks imported
          </span>
          {stats.needs_address > 0 && (
            <span className="inline-flex items-center gap-2 px-3 py-1 border-2 border-amber-500 bg-amber-50 text-amber-900 font-black uppercase text-xs tracking-wider" data-testid="clickup-needs-address">
              {stats.needs_address.toLocaleString()} need address triage
            </span>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <input type="file" accept=".csv,text/csv"
          onChange={e => setFile(e.target.files?.[0] || null)}
          data-testid="clickup-import-file"
          className="text-xs font-medium"/>
        <button onClick={upload} disabled={busy || !file}
          data-testid="clickup-import-upload-btn"
          className="tactile-btn h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-2">
          <FileArrowUp weight="fill" size={14}/> {busy ? "Importing…" : "Upload & Import"}
        </button>
      </div>
      {result && (
        <div className="mt-3 p-3 border-2 border-emerald-500 bg-emerald-50 text-xs font-mono" data-testid="clickup-import-result">
          <div className="font-black uppercase tracking-widest text-emerald-800 mb-1">Import Complete</div>
          <div>• Rows in file: <b>{result.total_rows}</b></div>
          <div>• Newly imported: <b>{result.imported}</b></div>
          <div>• Skipped duplicates (Task ID match): <b>{result.skipped_duplicates}</b></div>
          <div>• Skipped malformed (no Task Name): <b>{result.skipped_malformed}</b></div>
          <div>• Missing address (needs triage): <b>{result.missing_address}</b></div>
          <div>• New property configs seeded: <b>{result.new_property_configs}</b></div>
        </div>
      )}
    </section>
  );
}

function PartsCatalogSection() {
  const [parts, setParts] = useState([]);
  const [f, setF] = useState({ name: "", sku: "", category: "", unit_cost: "", sell_price: "" });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/parts").then(r => setParts(r.data));
  useEffect(() => { load(); }, []);
  const reset = () => { setF({ name: "", sku: "", category: "", unit_cost: "", sell_price: "" }); setEditingId(null); };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(), sku: f.sku.trim(), category: f.category.trim(),
        unit_cost: parseFloat(f.unit_cost) || 0, sell_price: parseFloat(f.sell_price) || 0, notes: "",
      };
      if (editingId) await api.patch(`/parts/${editingId}`, payload);
      else await api.post("/parts", payload);
      toast.success(editingId ? "Part updated" : "Part added");
      reset(); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const edit = (p) => { setEditingId(p.id); setF({ name: p.name, sku: p.sku || "", category: p.category || "", unit_cost: String(p.unit_cost || ""), sell_price: String(p.sell_price || "") }); };
  const del = async (id) => {
    if (!window.confirm("Delete this part?")) return;
    await api.delete(`/parts/${id}`);
    toast.success("Part removed"); await load();
  };
  const totalMargin = parts.reduce((s, p) => s + Math.max(0, (p.sell_price || 0) - (p.unit_cost || 0)) * (p.used_count || 0), 0);

  return (
    <section className="mt-8 rugged-card p-4 sm:p-6" data-testid="parts-catalog-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
          <Package weight="bold" size={22}/> Parts Catalog
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            const trade = window.prompt("Which trade? (e.g., HVAC, roofing, appliance repair, tile work, pool service)");
            if (!trade) return;
            try {
              toast.info("Claude is drafting suggested parts...");
              const { data } = await api.post("/parts/ai-suggest", { trade, notes: "" });
              if (!data.items?.length) { toast.error("No suggestions returned — try a different trade"); return; }
              const preview = data.items.map(i => `• ${i.name} — cost $${i.unit_cost.toFixed(2)} / sell $${i.sell_price.toFixed(2)}`).join("\n");
              if (!window.confirm(`Import ${data.items.length} suggested ${trade} parts?\n\n${preview.slice(0, 800)}${preview.length > 800 ? "\n..." : ""}`)) return;
              const r = await api.post("/parts/bulk-import", { parts: data.items });
              toast.success(`Added ${r.data.added} parts`);
              await load();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI suggest failed"); }
          }} data-testid="ai-suggest-parts-btn"
            className="h-10 px-3 border-2 border-[#00C4CC] text-[#00C4CC] font-black uppercase text-xs tracking-wider bg-white hover:bg-cyan-50">
            AI Suggest Parts
          </button>
          <span className="text-xs font-black uppercase tracking-widest text-slate-500">
            {parts.length} part{parts.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <p className="mt-1 text-sm text-slate-500 font-medium">
        Parts your techs carry on the truck. When they tap "Use From Truck Stock" on a job, the item is billed at your <b>sell price</b> so nothing slips through.
      </p>

      <form onSubmit={submit} className="mt-4 border-2 border-[#0f172a] p-3 bg-slate-50" data-testid="parts-form">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <input required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Part name *"
            data-testid="part-name" className="col-span-2 h-10 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          <input value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} placeholder="SKU"
            data-testid="part-sku" className="h-10 px-3 border-2 border-[#0f172a] text-sm font-medium font-mono"/>
          <input value={f.category} onChange={e => setF({ ...f, category: e.target.value })} placeholder="Category"
            data-testid="part-category" className="col-span-2 sm:col-span-2 h-10 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          <input type="number" step="0.01" value={f.unit_cost} onChange={e => setF({ ...f, unit_cost: e.target.value })} placeholder="Cost $"
            data-testid="part-cost" className="h-10 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
          <input type="number" step="0.01" value={f.sell_price} onChange={e => setF({ ...f, sell_price: e.target.value })} placeholder="Sell $"
            data-testid="part-sell" className="h-10 px-3 border-2 border-[#00C4CC] text-sm font-black text-[#00C4CC]"/>
          <div className="col-span-2 sm:col-span-3 flex gap-2">
            <button type="submit" disabled={busy} data-testid="part-submit"
              className="tactile-btn flex-1 h-10 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider disabled:opacity-50">
              <Plus weight="bold" size={14} className="inline mr-1"/> {editingId ? "Update Part" : "Add Part"}
            </button>
            {editingId && (
              <button type="button" onClick={reset}
                className="h-10 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white">
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="mt-4 overflow-x-auto" data-testid="parts-list">
        {parts.length === 0 ? (
          <div className="text-sm text-slate-500 font-medium py-4 text-center border-2 border-dashed border-slate-300">
            No parts yet. Add your truck stock above so techs never miss a billable item.
          </div>
        ) : (
          <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-[#0f172a] text-white text-xs font-black uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-right">Sell</th>
                <th className="px-3 py-2 text-right">Margin</th>
                <th className="px-3 py-2 text-right">Used</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {parts.map(p => {
                const margin = (p.sell_price || 0) - (p.unit_cost || 0);
                const marginPct = p.unit_cost > 0 ? (margin / p.unit_cost) * 100 : 0;
                return (
                  <tr key={p.id} className="border-t border-slate-200" data-testid={`part-row-${p.id}`}>
                    <td className="px-3 py-2 font-bold">{p.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.sku || "—"}</td>
                    <td className="px-3 py-2 text-xs">{p.category || "—"}</td>
                    <td className="px-3 py-2 text-right">${(p.unit_cost || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-black text-[#00C4CC]">${(p.sell_price || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      <span className={margin > 0 ? "text-green-600 font-bold" : "text-slate-400"}>
                        ${margin.toFixed(2)}{marginPct > 0 && ` (${marginPct.toFixed(0)}%)`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600">{p.used_count || 0}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => edit(p)} data-testid={`part-edit-${p.id}`}
                        className="text-xs font-bold text-[#0f172a] hover:underline mr-2">Edit</button>
                      <button onClick={() => del(p.id)} data-testid={`part-delete-${p.id}`}
                        className="text-xs font-bold text-red-600 hover:underline">×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ── Owner Approval Threshold — AppFolio-parity feature toggle ─────────────
function OwnerApprovalCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/owner-approval").then(r => setCfg(r.data)).catch(() => setCfg({ enabled: false, threshold_cents: 50000, message_note: "" }));
  }, []);
  const save = async (patch) => {
    if (!cfg) return;
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      await api.put("/settings/owner-approval", next);
      toast.success("Owner approval settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  if (!cfg) return null;
  const dollars = Math.round((cfg.threshold_cents || 0) / 100);
  return (
    <section className="mt-6 rugged-card p-6 border-l-4 border-emerald-500" data-testid="settings-owner-approval-card">
      <div className="flex items-center gap-2">
        <ShieldCheck weight="fill" size={22} className="text-emerald-600"/>
        <h2 className="font-display font-black text-xl uppercase">Owner Approval Threshold</h2>
        <span className={`ml-auto px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${cfg.enabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700"}`}
          data-testid="settings-owner-approval-status">
          {cfg.enabled ? "ACTIVE" : "OFF"}
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-600 font-medium">
        AppFolio-style safety net: when a work order's estimate crosses your threshold, tap <b>Request Owner Approval</b> on the WO to fire off a one-click Approve/Reject email to the landlord. Their decision is stamped on the WO for legal cover — no more "you never told me it'd be $2k".
      </p>

      <div className="mt-4 flex items-center justify-between p-3 border-2 border-emerald-300 bg-emerald-50">
        <div className="min-w-0">
          <div className="font-display font-black text-sm uppercase tracking-wide">Enable Owner Approval Flow</div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            When ON, an "Owner Approval" panel appears on every WO with a one-tap request button.
          </div>
        </div>
        <button
          data-testid="settings-owner-approval-toggle"
          onClick={() => save({ enabled: !cfg.enabled })}
          disabled={saving}
          className={`h-9 px-4 font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] ${cfg.enabled ? "bg-emerald-600 text-white" : "bg-white border-2 border-slate-900 text-slate-900"}`}>
          {cfg.enabled ? "ON — TAP TO DISABLE" : "TURN ON"}
        </button>
      </div>

      {cfg.enabled && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-black uppercase tracking-wider flex items-center gap-1">
              <CurrencyDollar weight="bold" size={13}/> Threshold Dollar Amount
            </label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-slate-500 font-black">$</span>
              <input type="number" min="0" step="50" value={dollars}
                onChange={(e) => setCfg({ ...cfg, threshold_cents: Math.max(0, parseInt(e.target.value || "0", 10)) * 100 })}
                onBlur={() => save({ threshold_cents: cfg.threshold_cents })}
                data-testid="settings-owner-approval-threshold"
                className="w-32 h-11 px-3 border-2 border-[#0f172a] font-mono font-black text-lg"/>
              <span className="text-xs font-medium text-slate-500">
                Trigger the approval flow when an estimate exceeds this.
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider">Optional Note to Owner</label>
            <textarea
              value={cfg.message_note || ""}
              onChange={(e) => setCfg({ ...cfg, message_note: e.target.value })}
              onBlur={() => save({ message_note: cfg.message_note })}
              placeholder="e.g. Please respond within 24 hours so we can dispatch our vendor."
              rows={2}
              data-testid="settings-owner-approval-note"
              className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium"/>
          </div>
        </div>
      )}
    </section>
  );
}

